import { ProductDrawerProps } from './types'
import { useTranslation } from 'next-i18next/pages'
import parse from 'html-react-parser'
import { formatAmount } from 'utils/format'
import { Button, Drawer, Heading, Toggle } from '@boltenergy-be/design-system'
import styles from '../../components/product-drawer/ProductDrawer.module.scss'
import BlocksRenderer from 'components/blocks-renderer/BlocksRenderer'
import { useStoreSelector } from 'hooks/store'
import { startSimulationOrRegistration } from 'utils/customerFlow'
import { useEffect, useState } from 'react'
import { DURATION_CODES, Product, PRODUCT_BILLING_OPTIONS, PRODUCT_CONFIG_OPTIONS, PRODUCTS, PRODUCTS_CONFIG } from '@boltenergy-be/shared'

const ProductDrawer = ({
  product: propsProduct,
  isOpen,
  onRequestClose,
  pricelists,
  userType,
  counterpart,
  cmsProducts,
  extraStartSimulationAction,
  platformCostReduction,
  hideAction
}: ProductDrawerProps) => {
  // Redux
  const { region } = useStoreSelector((store) => store.customer.inputs.simulation)

  // i18n
  const { t } = useTranslation(['pricing', 'common'])

  // Local state
  const [product, setProduct] = useState<Product>(propsProduct)

  // Initialize selected product when the drawer opens
  useEffect(() => {
    if (propsProduct) setProduct(propsProduct)
  }, [propsProduct])

  if (!product) return null

  // Constants
  const propsProductContent = cmsProducts.find((cmsProduct) => cmsProduct.apiProduct.slug === propsProduct)
  const selectedCounterpartContent = counterpart && cmsProducts.find((cmsProduct) => cmsProduct.apiProduct.slug === counterpart)
  const displayedProductContent = cmsProducts.find((cmsProduct) => cmsProduct.apiProduct.slug === product)
  const productName = displayedProductContent?.name
  const elPricelist = product === propsProduct ? pricelists?.electricity : pricelists?.electricityCounterpart
  const gasPricelist = product === propsProduct ? pricelists?.gas : pricelists?.gasCounterpart
  const shouldPrefixWithBolt = [PRODUCTS.fix, PRODUCTS.variableOnline, PRODUCTS.variableOffline].some((value) => value === product)
  const prefixedProductName = shouldPrefixWithBolt ? `Bolt ${productName}` : productName
  const defaultPlatformCost = PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.platformCost][userType]
  const platformCost = platformCostReduction
    ? defaultPlatformCost - defaultPlatformCost * Number(platformCostReduction)
    : defaultPlatformCost

  return (
    <Drawer title={propsProductContent?.name} className={styles.drawer} {...{ isOpen, onRequestClose }}>
      {product && counterpart && (
        <Toggle
          active={product}
          onClick={(p) => setProduct(p)}
          isFullwidth
          options={[
            { label: `Bolt ${propsProductContent?.name}`, value: propsProduct },
            { label: selectedCounterpartContent?.name, value: counterpart }
          ]}
        />
      )}

      <>
        <section>
          <ul>
            <li>{parse(t('drawer.intro.connections'))}</li>
            <li>{parse(t('drawer.intro.origins.el'))}</li>
            <li>{parse(t('drawer.intro.origins.gas'))}</li>
            <li>
              {parse(
                t('drawer.intro.type', {
                  type: t(`common:${PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.type]}`)
                })
              )}
            </li>
            <li>
              <strong>{t('drawer.intro.duration.label')}:</strong>{' '}
              {PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.durationCode] === DURATION_CODES.unlimited
                ? t('drawer.intro.duration.unlimited')
                : t(`drawer.intro.duration.yearBased`, {
                    count: Number(PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.durationCode]) / 12
                  })}
            </li>
          </ul>
        </section>

        <section>
          <ul>
            {elPricelist && (
              <li>
                <a href={`https://${elPricelist}`} target="_blank" rel="noreferrer">
                  {t('drawer.pricelist.el')}
                </a>
              </li>
            )}
            {gasPricelist && (
              <li>
                <a href={`https://${gasPricelist}`} target="_blank" rel="noreferrer">
                  {t('drawer.pricelist.gas')}
                </a>
              </li>
            )}
          </ul>
        </section>

        <section>
          <Heading as="h2" variant="h6">
            {t('drawer.structure.title')}
          </Heading>
          <p>{t('drawer.structure.description')}</p>
          <table>
            <thead>
              <tr>
                <th scope="col">{t('drawer.structure.table.payment.head')}</th>
                <th scope="col">{t('drawer.structure.table.receiver.head')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('drawer.structure.table.payment.el')}</td>
                <td>{t('drawer.structure.table.receiver.el')}</td>
              </tr>
              <tr>
                <td>{t('drawer.structure.table.payment.gas')}</td>
                <td>{t('drawer.structure.table.receiver.gas')}</td>
              </tr>
              <tr>
                <td>{t('drawer.structure.table.payment.net')}</td>
                <td>{t('drawer.structure.table.receiver.net')}</td>
              </tr>
              <tr>
                <td>{t('drawer.structure.table.payment.platform')}</td>
                <td>{t('drawer.structure.table.receiver.platform')}</td>
              </tr>
              <tr>
                <td>{t('drawer.structure.table.payment.taxes')}</td>
                <td>{t('drawer.structure.table.receiver.taxes')}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <Heading as="h2" variant="h6">
            {t('drawer.platformCost.title', {
              cost: formatAmount(platformCost)
            })}
          </Heading>
          <p>{t(`drawer.platformCost.description.${product}`)}</p>
          <table>
            <tbody>
              <tr>
                <th scope="row">{t(`drawer.table.${PRODUCT_CONFIG_OPTIONS.instalmentPeriodicity}`)}</th>
                <td>{t(`common:periodicity.${PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.instalmentPeriodicity]}`)}</td>
              </tr>
              <tr>
                <th scope="row">{t(`drawer.table.${PRODUCT_CONFIG_OPTIONS.directDebitMandatory}`)}</th>
                <td>
                  {PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.directDebitMandatory] ? t('common:mandatory') : t('common:optional')}
                </td>
              </tr>
              <tr>
                <th scope="row">{t(`drawer.table.${PRODUCT_CONFIG_OPTIONS.billingOptions}`)}</th>
                <td>
                  {t('drawer.platformCost.table.option.billingOptionsValue', {
                    options: PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.billingOptions]
                      .map((option) => t(`common:${option === PRODUCT_BILLING_OPTIONS.digital ? 'email' : option}`).toLowerCase())
                      .join(` ${t('common:andOr')} `)
                  })}
                </td>
              </tr>
              <tr>
                <th scope="row">{t(`drawer.table.${PRODUCT_CONFIG_OPTIONS.contactOptions}`)}</th>
                <td>
                  {PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.contactOptions].map((option) => t(`common:${option}`)).join(' & ')}
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        {displayedProductContent.conditions && (
          <section className={styles.conditions}>
            <Heading as="h2" variant="h6">
              {t('drawer.conditions.title')}
            </Heading>
            <BlocksRenderer content={displayedProductContent.conditions} />
          </section>
        )}

        {!hideAction && (
          <Button
            className="mt-400"
            isFullwidth
            disabled={region && !PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.availableInRegions].includes(region)}
            onClick={async () => {
              await extraStartSimulationAction?.()
              startSimulationOrRegistration({
                specificProduct: product
              })
            }}
          >
            {t('chooseProduct', { productName: prefixedProductName })}
          </Button>
        )}
      </>
    </Drawer>
  )
}

export default ProductDrawer
